import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import _ from 'lodash';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import UpdateOrderLineItems from '../../../services/UpdateOrderLineItems';
import VB0v3DeviceSectionView from '../../../views/VB0v3DeviceSectionView/VB0v3DeviceSectionView';

import tmplCONUSFormView from './VB0v3FormView.hbs';

const VB0v3FormView = View.extend({
  template: tmplCONUSFormView,

  ui: {
    vb0DescriptionSection: '.vb0-description-section',
    vb0NiprMfdSection: '.vb0-nipr-mfd-section',
    vb0SiprMfdSection: '.vb0-sipr-mfd-section',
    vb0NiprPrinterSection: '.vb0-nipr-printer-section',
    vb0SiprPrinterSection: '.vb0-sipr-printer-section',
    vb0NiprScannerSection: '.vb0-nipr-scanner-section',
    vb0TonerSection: '.vb0-toner-section',
    vb0PaperTraySection: '.vb0-paper-tray-section',
    devicesAlert: '.no-devices-error'
  },

  regions: {
    vb0DescriptionSection: '@ui.vb0DescriptionSection',
    vb0NiprMfdSection: '@ui.vb0NiprMfdSection',
    vb0SiprMfdSection: '@ui.vb0SiprMfdSection',
    vb0NiprPrinterSection: '@ui.vb0NiprPrinterSection',
    vb0SiprPrinterSection: '@ui.vb0SiprPrinterSection',
    vb0NiprScannerSection: '@ui.vb0NiprScannerSection',
    vb0TonerSection: '@ui.vb0TonerSection',
    vb0PaperTraySection: '@ui.vb0PaperTraySection'
  },

  className: 'vb0-v3-form-view',

  onRender() {
    this.vb0DeviceSectionView = new VB0v3DeviceSectionView();
    const orderType = this.model.get('orderDetails').orderTypeId;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    const {
      NIPR_MFD,
      SIPR_MFD,
      NIPR_Printer,
      SIPR_Printer,
      NIPR_Scanner,
      Toner,
      'Paper Tray': Paper_Tray
    } = _.groupBy(this.model.get('lineItems'), 'panel');

    this.showChildView(
      'vb0DescriptionSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          contract: orderType === '20' ? 'VB0 Conus' : 'VB0 OConus',
          id: 'Conus-Description',
          deviceMatrix: this.model.get('deviceMatrix'),
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0NiprMfdSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: NIPR_MFD,
          title: 'NIPR Desktop Multifunctional Devices (MFDs)',
          id: 'niprMfd',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0SiprMfdSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: SIPR_MFD,
          title: 'SIPR Desktop Multifunctional Devices (MFDs)',
          id: 'siprMfd',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0NiprPrinterSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: NIPR_Printer,
          title: 'NIPR Desktop Single-Function Printers',
          id: 'niprPrinter',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0SiprPrinterSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: SIPR_Printer,
          title: 'SIPR Desktop Single-Function Printers',
          id: 'siprPrinter',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0NiprScannerSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: NIPR_Scanner,
          title: 'NIPR Desktop Scanners',
          id: 'niprScanners',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );
    this.showChildView(
      'vb0TonerSection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: Toner,
          title: 'Toner for MFDs and Printers',
          id: 'toner',
          orderType,
          type: 'optionalItems',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0PaperTraySection',
      new VB0v3DeviceSectionView({
        model: new Backbone.Model({
          lineItems: Paper_Tray,
          title: 'Paper Trays for MFDs and Printers',
          id: 'paperTray',
          orderType,
          type: 'optionalItems',
          isAdminEdit
        })
      })
    );
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  showNoChangedDevicesError() {
    showErrorMessage({
      errorCode: 'noChangedDevicesError',
      errorTitle: 'No Devices Updated Error',
      errorMessage: 'No devices were updated. Please update devices in order to save changes.'
    });
  },

  async validate() {
    const [
      niprMfdColl,
      siprMfdColl,
      niprPrinterColl,
      siprPrinterColl,
      vb0NiprScannerColl,
      vb0TonerColl,
      vb0PaperTrayColl
    ] = await this.getDeviceData();

    let isValid = true;
    if (
      !niprMfdColl.totalNiprMfdQuantity &&
      !siprMfdColl.totalSiprMfdQuantity &&
      !niprPrinterColl.totalNiprPrinterQuantity &&
      !siprPrinterColl.totalSiprPrinterQuantity &&
      !vb0NiprScannerColl.totalNiprScannerQuantity &&
      !vb0TonerColl.totalTonerQuantity &&
      !vb0PaperTrayColl.totalPaperTrayQuantity
    ) {
      isValid = false;
      this.ui.devicesAlert.show();
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  async getDeviceData() {
    return await Promise.all([
      this.getChildView('vb0NiprMfdSection').getDeviceData(),
      this.getChildView('vb0SiprMfdSection').getDeviceData(),
      this.getChildView('vb0NiprPrinterSection').getDeviceData(),
      this.getChildView('vb0SiprPrinterSection').getDeviceData(),
      this.getChildView('vb0NiprScannerSection').getDeviceData(),
      this.getChildView('vb0TonerSection').getDeviceData(),
      this.getChildView('vb0PaperTraySection').getDeviceData()
    ]);
  },

  getLineItemData(lineItemData) {
    const lineItems = this.model.get('lineItems');
    const changedItems = [];

    _.each(lineItems, (item) => {
      const changedPrice = lineItemData[`${item.id}-price`];
      const changedWarrantyYear = lineItemData[`${item.id}-warrantyYear`];
      const changedPpm = lineItemData[`${item.id}-ppm`];
      const changedDesc = lineItemData[`${item.id}-description`];
      const changedModel = lineItemData[`${item.id}-model`];
      const changedCompatibleDevice = lineItemData[`${item.id}-compatibleDevice`];
      const changedDlaCost = lineItemData[`${item.id}-dlaCost`];

      const isPriceChanged = item.price !== changedPrice && item.price;
      const isWarrantyYearChanged = item.warrantyYear !== changedWarrantyYear && item.warrantyYear;
      const isPpmChanged = item.speedPPM !== changedPpm && item.speedPPM;
      const isDescChanged = item.description !== changedDesc && item.description;
      const isModelChanged = item.model !== changedModel && item.model;
      const isCompatibleDeviceChanged = item.compatibleDevice !== changedCompatibleDevice && item.compatibleDevice;
      const isDlaCostChanged = item.dlaCost !== changedDlaCost && !!changedDlaCost;

      const changedItem = {};
      if (isPriceChanged) changedItem.price = parseFloat(changedPrice).toFixed(2);
      if (isWarrantyYearChanged) changedItem.warrantyYear = changedWarrantyYear;
      if (isPpmChanged) changedItem.speedPPM = changedPpm;
      if (isDescChanged) changedItem.description = changedDesc;
      if (isModelChanged) changedItem.model = changedModel;
      if (isCompatibleDeviceChanged) changedItem.compatibleDevice = changedCompatibleDevice;
      if (isDlaCostChanged) changedItem.dlaCost = changedDlaCost;
      if (!_.isEmpty(changedItem)) {
        changedItem.id = item.id;
        changedItems.push(changedItem);
      }
    });

    return changedItems;
  },

  async onSave(lineItemData) {
    this.ui.devicesAlert.hide();
    this.trigger('no:errors');
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;
    let formData;

    try {
      if (isAdminEdit && lineItemData) {
        formData = await this.saveAdminEdit(lineItemData);
        if (formData.changedItems.length > 0) {
          await UpdateOrderLineItems(formData);
          this.showSaveSuccess();
        } else {
          this.showNoChangedDevicesError();
        }
      } else {
        formData = await this.saveOrder();
        await Promise.all([SaveCustomerOrderDevices(formData), ReloadViewModule.reload()]);
        this.showSaveSuccess();
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  async saveOrder() {
    const [
      niprMfdColl,
      siprMfdColl,
      niprPrinterColl,
      siprPrinterColl,
      vb0NiprScannerColl,
      vb0TonerColl,
      vb0PaperTrayColl
    ] = await this.getDeviceData();

    const devicesFormData = niprMfdColl.changedItems.concat(
      siprMfdColl.changedItems,
      niprPrinterColl.changedItems,
      siprPrinterColl.changedItems,
      vb0NiprScannerColl.changedItems,
      vb0TonerColl.changedItems,
      vb0PaperTrayColl.changedItems
    );

    const totalMfdPrice = niprMfdColl?.totalNiprMfdPrice + siprMfdColl?.totalSiprMfdPrice || 0;
    const totalMfdQuantity = niprMfdColl?.totalNiprMfdQuantity + siprMfdColl?.totalSiprMfdQuantity || 0;

    const totalPrinterPrice = niprPrinterColl?.totalNiprPrinterPrice + siprPrinterColl?.totalSiprPrinterPrice || 0;
    const totalPrinterQuantity =
      niprPrinterColl?.totalNiprPrinterQuantity + siprPrinterColl?.totalSiprPrinterQuantity || 0;

    const totalScannerPrice = vb0NiprScannerColl?.totalNiprScannerPrice || 0;
    const totalScannerQuantity = vb0NiprScannerColl?.totalNiprScannerQuantity || 0;

    const totalTonerPrice = vb0TonerColl?.totalTonerPrice || 0;
    const totalTonerQuantity = vb0TonerColl?.totalTonerQuantity || 0;

    const totalPaperTrayPrice = vb0PaperTrayColl?.totalPaperTrayPrice || 0;
    const totalPaperTrayQuantity = vb0PaperTrayColl?.totalPaperTrayQuantity || 0;

    const totalOrderPrice =
      totalMfdPrice + totalPrinterPrice + totalScannerPrice + totalTonerPrice + totalPaperTrayPrice;
    const totalOrderQuantity =
      totalMfdQuantity + totalPrinterQuantity + totalScannerQuantity + totalTonerQuantity + totalPaperTrayQuantity;

    const orderFormData = {
      totalOrderPrice,
      totalOrderQuantity,
      totalMfdPrice,
      totalMfdQuantity,
      totalPrinterPrice,
      totalPrinterQuantity,
      totalScannerPrice,
      totalScannerQuantity,
      totalTonerPrice,
      totalTonerQuantity,
      totalPaperTrayPrice,
      totalPaperTrayQuantity
    };
    const formData = {
      devicesFormData,
      orderFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    return formData;
  },

  saveAdminEdit(lineItemData) {
    const changedItems = this.getLineItemData(lineItemData);
    const formData = {
      orderFormTypeID: this.model.get('orderDetails').orderTypeId,
      changedItems
    };

    return formData;
  }
});

export default VB0v3FormView;
